import type { HpApiResponseDto, HpUserCredentialsDto, HpUserInteraction } from '@zg-rentals/ts-hotpads-api-client';
import { normalizeV2Verified } from '@/src/lib/auth/v2-verification';
import { apiClient } from '../client/apiClient';
import { fetchCsrfToken } from '../client/fetchCsrfToken';
import { assertHpApiV2Success } from '../hpApiV2Result';
import type { HpApiV2Result } from '../hpApiV2Result';

// ============================================================================
// Types
// ============================================================================

export type UserInfo = {
  email: string;
  firstName: string;
  forwardUrl?: string;
  lastName: string;
  passwordSet: boolean;
  passwordStatus: string;
  roles: Array<string>;
  hasOptedOutAdTracking: boolean;
};

export type UserCreds = {
  userToken: string;
  csrfToken: string;
  sessionToken: string;
  verified: boolean;
};

export type AuthResponse = {
  success: boolean;
  loggedIn: boolean;
  verified: boolean;
  status: string;
  data: UserInfo;
  creds: UserCreds;
  forwardUrl?: string;
};

type RawV2AuthResponse = Omit<AuthResponse, 'verified'> & {
  verified?: boolean;
};

/** Minimal user data safe to inject into HTML (no tokens/credentials) */
export type InitialUserData = {
  loggedIn: boolean;
  // photoUrl is frontend-only (Google avatar cookie); hotpads-api has no photo field
  data: Pick<UserInfo, 'email' | 'firstName' | 'lastName' | 'roles'> &
    Partial<Pick<UserInfo, 'passwordSet'>> & { photoUrl?: string };
};

/** The interactions the user dashboard lists. */
export type UserListingType =
  | HpUserInteraction.FAVORITE
  | HpUserInteraction.HIDE
  | HpUserInteraction.INQUIRY
  | HpUserInteraction.VIEW;

export type UpdateProfileBody = {
  firstName?: string;
  lastName?: string;
  email?: string;
  oldPassword?: string;
  password?: string;
  verificationToken?: string;
};

export type UserFeedbackBody = {
  userName: string;
  userEmail: string;
  comment: string;
};

export type RenterProfileApiData = {
  moveInDate?: Array<number>;
  moveInTimeFrame?: string;
  leaseLengthMonths?: number;
  incomeYearly?: number;
  numBedrooms?: number;
  numOccupants?: number;
  petDetails?: Array<{ type?: string }>;
  creditScoreMin?: number;
  creditScoreMax?: number;
  desiredParking?: string;
};

export type RenterProfileResponse = {
  success: boolean;
  loggedIn: boolean;
  data: RenterProfileApiData;
  creds: UserCreds;
};

export type RenterProfileRequestBody = {
  moveInDate?: string;
  moveInTimeFrame?: string;
  leaseLengthMonths?: number;
  numOccupants?: number;
  incomeYearly?: number;
  creditScoreMin?: number;
  creditScoreMax?: number;
  numBedrooms?: number;
  desiredPets?: boolean;
  desiredParking?: boolean;
};

export type SavedSearchFilter = {
  bedrooms?: string;
  bathrooms?: string;
  propertyTypes?: string;
  listingTypes?: string;
  minLat?: number;
  maxLat?: number;
  minLon?: number;
  maxLon?: number;
  // Typed Zillow scope identity. A real legacy `areas` id may accompany it,
  // but typed-only areas populate just the field matching their scope type.
  // Reads tolerate strings — the BE may echo them back stringified.
  zillowRegionIds?: number | string;
  zillowSchoolIds?: number | string;
  zillowSchoolDistrictIds?: number | string;
  search?: {
    slug?: string;
    title?: string;
  };
  [key: string]: unknown;
};

export type SavedSearchItem = {
  id?: string;
  searchId?: string;
  name?: string;
  email?: string;
  toEmail?: string;
  frequency?: string;
  emailFrequency?: string;
  filter?: SavedSearchFilter;
  mapData?: Record<string, unknown>;
  areaId?: string | number;
  borderAreaId?: string | number;
  resourceId?: string;
  url?: string;
  imageUrl?: string;
  created?: number;
  lastRun?: number;
  numberOfNewResults?: number;
  searchFilterType?: string;
  sid?: string;
};

export type SavedSearchListResponse = {
  success: boolean;
  loggedIn: boolean;
  data: Array<SavedSearchItem>;
  creds: UserCreds;
};

export type SavedSearchMutationResponse = {
  success: boolean;
  loggedIn: boolean;
  status?: string;
  message?: string;
  // The created/updated search preview — NOT user info like AuthResponse carries (HP-8359)
  data?: SavedSearchItem;
  creds?: UserCreds;
};

export type RecentSearchAreaInfo = {
  resourceId?: string;
  name?: string;
  city?: string;
  state?: string;
  type?: string;
  [key: string]: unknown;
};

export type RecentSearchData = {
  filter?: SavedSearchFilter;
  areaInfo?: RecentSearchAreaInfo;
};

export type RecentSearchResponse = {
  success: boolean;
  loggedIn: boolean;
  data?: RecentSearchData;
  creds: UserCreds;
};

// Geo (areas/lat/lon/bounds) rides flat inside `filter`, matching the legacy caller.
export type RecentSearchUpdateBody = {
  filter: SavedSearchFilter;
};

export type RecentSearchMutationResponse = {
  success: boolean;
  loggedIn?: boolean;
  creds?: UserCreds;
};

// ============================================================================
// Auth Endpoints
// ============================================================================

export async function emailPasswordLogin(email: string, password: string): Promise<AuthResponse> {
  const csrfToken = await fetchCsrfToken();

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  return apiClient<AuthResponse>({
    method: 'POST',
    path: '/v2/user/auth/emailPassword',
    body: { email, password },
    headers,
  });
}

export async function getUserInfo(): Promise<AuthResponse> {
  return apiClient<AuthResponse>({ path: '/v2/user/info' });
}

export async function logout(): Promise<void> {
  await apiClient<void>({ path: '/v2/user/auth/logout' });
}

// ============================================================================
// v3 response normalisation
// ============================================================================

type HpApiV3AuthResponse = HpApiResponseDto<HpUserCredentialsDto>;

function normalizeV3Response(v3: HpApiV3AuthResponse): AuthResponse {
  return {
    success: !v3.error,
    loggedIn: v3.session.loggedIn,
    verified: v3.session.verified,
    // v3 errors are generic FORBIDDEN codes with no structured status value;
    // callers fall through to their status || 'fallback' guard.
    status: '',
    data: {
      email: v3.data?.email ?? '',
      firstName: v3.data?.firstName ?? '',
      lastName: v3.data?.lastName ?? '',
      passwordSet: v3.data?.passwordSet ?? false,
      passwordStatus: v3.data?.passwordStatus ?? '',
      roles: v3.data?.roles ?? [],
      hasOptedOutAdTracking: v3.data?.hasOptedOutAdTracking ?? false,
    },
    creds: {
      userToken: v3.session.userToken ?? '',
      csrfToken: v3.session.csrfToken ?? '',
      sessionToken: v3.session.sessionToken ?? '',
      verified: v3.session.verified,
    },
  };
}

export async function googleAuth(idToken: string): Promise<AuthResponse> {
  const csrfToken = await fetchCsrfToken();

  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  const v3 = await apiClient<HpApiV3AuthResponse>({
    method: 'POST',
    path: '/web/v3/user/auth/google',
    body: { idToken },
    headers,
  });
  return normalizeV3Response(v3);
}

export async function appleAuth(userJwt: string): Promise<AuthResponse> {
  const csrfToken = await fetchCsrfToken();

  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  const v3 = await apiClient<HpApiV3AuthResponse>({
    method: 'POST',
    path: '/web/v3/user/auth/apple',
    body: { userJwt },
    headers,
  });
  return normalizeV3Response(v3);
}

export async function freepassAuth(email: string, authenticator: string): Promise<AuthResponse> {
  const csrfToken = await fetchCsrfToken();

  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  const v3 = await apiClient<HpApiV3AuthResponse>({
    method: 'POST',
    path: '/web/v3/user/auth/freepass',
    body: { email, authenticator },
    headers,
  });
  return normalizeV3Response(v3);
}

export async function sendResetPasswordEmail(email: string): Promise<AuthResponse> {
  const csrfToken = await fetchCsrfToken();

  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  return apiClient<AuthResponse>({
    method: 'POST',
    path: '/v2/user/auth/sendResetPasswordEmail',
    body: { email },
    headers,
  });
}

// ============================================================================
// User Profile Endpoints
// ============================================================================

export async function updateProfile(body: UpdateProfileBody): Promise<AuthResponse> {
  const csrfToken = await fetchCsrfToken();

  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  const response = await apiClient<RawV2AuthResponse>({
    method: 'PUT',
    path: '/v2/user/info/updateV2',
    body,
    headers,
  });

  return { ...response, verified: normalizeV2Verified(response) };
}

export async function sendFeedback(body: UserFeedbackBody): Promise<void> {
  const csrfToken = await fetchCsrfToken();

  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  // rpjava HpApiUserHandler.feedbackJson returns HpApiResult<Boolean>
  const response = await apiClient<HpApiV2Result<boolean>>({
    method: 'POST',
    path: '/v2/user/feedbackJson',
    body,
    headers,
  });
  assertHpApiV2Success(response, 'the feedback');
}

// ============================================================================
// Renter Profile Endpoints
// ============================================================================

export async function getRenterProfile(): Promise<RenterProfileResponse> {
  return apiClient<RenterProfileResponse>({
    path: '/v2/user/renterProfile/get',
  });
}

export async function updateRenterProfile(body: RenterProfileRequestBody): Promise<RenterProfileResponse> {
  const csrfToken = await fetchCsrfToken();
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  return apiClient<RenterProfileResponse>({
    method: 'POST',
    path: '/v2/user/renterProfile/createOrUpdatev2',
    body,
    headers,
  });
}

// ============================================================================
// Saved Search Endpoints
// ============================================================================

export async function getSavedSearches(): Promise<SavedSearchListResponse> {
  return apiClient<SavedSearchListResponse>({
    path: '/v2/user/savedSearch/list',
  });
}

export async function updateSavedSearch(body: SavedSearchItem): Promise<SavedSearchMutationResponse> {
  const csrfToken = await fetchCsrfToken();

  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  const response = await apiClient<SavedSearchMutationResponse>({
    method: 'PUT',
    path: '/v2/user/savedSearch/update',
    body,
    headers,
  });
  return assertHpApiV2Success(response, 'the saved search update');
}

export async function deleteSavedSearch(searchId: string): Promise<void> {
  const csrfToken = await fetchCsrfToken();

  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  // rpjava HpApiSavedSearchHandler.delete returns HpApiResult<Boolean>
  const response = await apiClient<HpApiV2Result<boolean>>({
    method: 'DELETE',
    path: '/v2/user/savedSearch/delete',
    queryParams: { searchId },
    headers,
  });
  assertHpApiV2Success(response, 'the saved search delete');
}

// ============================================================================
// Recent Search Endpoints
// ============================================================================

export async function getRecentSearch(): Promise<RecentSearchResponse> {
  return apiClient<RecentSearchResponse>({
    path: '/v2/user/recentSearch',
  });
}

export async function updateRecentSearch(body: RecentSearchUpdateBody): Promise<RecentSearchMutationResponse> {
  const csrfToken = await fetchCsrfToken();

  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  return apiClient<RecentSearchMutationResponse>({
    method: 'PUT',
    path: '/v2/user/recentSearch/update',
    body,
    headers,
  });
}

export type NetPromoterScoreBody = {
  email?: string;
  userToken?: string;
  score?: string | number;
  comment?: string;
  date: number;
};

export async function updateNetPromoterScore({
  email,
  userToken,
  score,
  comment,
  date,
}: NetPromoterScoreBody): Promise<void> {
  const csrfToken = await fetchCsrfToken();
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  // rpjava HpApiUserHandler.updateNetPromoterScore returns HpApiResult<ApiV2NetPromoterScoreDto>
  const response = await apiClient<HpApiV2Result<unknown>>({
    method: 'POST',
    path: '/v2/user/updateNetPromoterScore',
    body: {
      email,
      userToken,
      npsScore: score,
      comment,
      sourceType: 'EMAIL',
      date,
    },
    headers,
  });
  assertHpApiV2Success(response, 'the Net Promoter Score');
}

export async function reactivateRecommendedSearch(email: string): Promise<void> {
  const csrfToken = await fetchCsrfToken();
  const headers: Record<string, string> = {};
  if (csrfToken) {
    headers.csrfToken = csrfToken;
  }

  // rpjava HpApiRecommendedSearchHandler.reactivate returns HpApiResult<Boolean>
  const response = await apiClient<HpApiV2Result<boolean>>({
    method: 'PUT',
    path: '/v2/user/recommendedSearch/reactivate',
    queryParams: { email },
    headers,
  });
  assertHpApiV2Success(response, 'the recommended search reactivation');
}

// ============================================================================
// Namespace Export
// ============================================================================

export const user = {
  emailPasswordLogin,
  getUserInfo,
  logout,
  googleAuth,
  appleAuth,
  freepassAuth,
  sendResetPasswordEmail,
  updateProfile,
  sendFeedback,
  getRenterProfile,
  updateRenterProfile,
  getSavedSearches,
  updateSavedSearch,
  deleteSavedSearch,
  getRecentSearch,
  updateRecentSearch,
  updateNetPromoterScore,
  reactivateRecommendedSearch,
};
